import { createClient } from "@/lib/supabase/client";
import { User } from "@supabase/supabase-js";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useActionState } from "react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";

type StateType = {
  error?: string | undefined;
  success: boolean;
  user?: any;
};

export default function ChangeEmail({ user }: { user?: User | null }) {
  const supabase = createClient();

  const [emailState, emailSubmitAction, emailIsPending] = useActionState(
    async (_previousState: any, formData: FormData): Promise<StateType> => {
      const email = formData.get("email");

      if (!email || !email.toString()) {
        return {
          error: "Email cannot be empty",
          success: false,
        };
      }

      const { data, error } = await supabase.auth.updateUser({
        email: email.toString(),
      });

      if (error) {
        return {
          error: error.message,
          success: false,
        };
      }

      return {
        success: true,
        user: data.user,
      };
    },
    null,
  );

  const checkError = (state: StateType | null): boolean => {
    if (!state) {
      return false;
    }
    if (state.success) {
      return false;
    }

    return true;
  };

  return (
    <form action={emailSubmitAction}>
      <Card id="email">
        <CardHeader>
          <CardTitle>Email</CardTitle>
          <CardDescription>Change your email</CardDescription>
        </CardHeader>
        <CardContent>
          {checkError(emailState) && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-6 w-6" />
              <AlertTitle className="!pl-8">Error</AlertTitle>
              <AlertDescription className="!pl-8">
                {emailState?.error}
              </AlertDescription>
            </Alert>
          )}
          {!checkError(emailState) && emailState?.success && (
            <Alert variant="default" className="mb-4">
              <CheckCircle2 className="h-6 w-6" />
              <AlertTitle className="!pl-8">Success</AlertTitle>
              <AlertDescription className="!pl-8">
                Email updated successfully
              </AlertDescription>
            </Alert>
          )}
          {!user ? (
            <Skeleton className="w-full h-10" />
          ) : (
            <Input
              placeholder="New email"
              type="email"
              className=""
              name="email"
            />
          )}
        </CardContent>
        <CardFooter className="border-t px-6 py-4">
          {!user ? (
            <Skeleton className="w-[150px] h-10" />
          ) : (
            <Button disabled={emailIsPending}>
              {emailIsPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}{" "}
              Change email
            </Button>
          )}
        </CardFooter>
      </Card>
    </form>
  );
}
